import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { useNavigate, useLocation } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import L from 'leaflet';
import './LocationPickerPage.css';

// Fix for default markers in react-leaflet - using direct URLs
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const LocationPickerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawnLayers, setDrawnLayers] = useState([]);
  const [selectedGeometry, setSelectedGeometry] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const featureGroupRef = React.useRef();
  const editControlRef = React.useRef();

  // Default map center (Thunder Bay, Ontario)
  const mapCenter = [48.3809, -89.2477];
  const mapZoom = 10;

  // Load existing location when editing
  useEffect(() => {
    if (location.state?.existingLocation && mapReady && featureGroupRef.current) {
      try {
        const existingGeometry = location.state.existingLocation;
        console.log('Loading existing geometry:', existingGeometry);
        
        // Clear any existing layers first
        featureGroupRef.current.clearLayers();
        
        let layer;
        
        // Create the appropriate Leaflet layer based on geometry type
        if (existingGeometry.type === 'Point') {
          const [lng, lat] = existingGeometry.coordinates;
          layer = L.marker([lat, lng]);
        } else if (existingGeometry.type === 'LineString') {
          const latlngs = existingGeometry.coordinates.map(([lng, lat]) => [lat, lng]);
          layer = L.polyline(latlngs, {
            color: '#ff7800',
            weight: 3,
            opacity: 0.8
          });
        } else if (existingGeometry.type === 'Polygon') {
          const latlngs = existingGeometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
          layer = L.polygon(latlngs, {
            color: '#ff7800',
            weight: 2,
            opacity: 0.8,
            fillOpacity: 0.35
          });
        } else if (existingGeometry.type === 'MultiPoint') {
          // For MultiPoint, create multiple markers (add them individually)
          existingGeometry.coordinates.forEach(([lng, lat]) => {
            const marker = L.marker([lat, lng]);
            featureGroupRef.current.addLayer(marker);
            const geojson = marker.toGeoJSON();
            setDrawnLayers(prev => [...prev, { id: Date.now() + Math.random(), layer: marker, geojson }]);
          });
          updateSelectedGeometry([{ geojson: { geometry: existingGeometry } }]);
          return; // Exit early for MultiPoint
        }
        
        if (layer) {
          featureGroupRef.current.addLayer(layer);
          const geojson = layer.toGeoJSON();
          const loadedLayer = { id: Date.now(), layer, geojson };
          setDrawnLayers([loadedLayer]);
          updateSelectedGeometry([loadedLayer]);
        }
      } catch (error) {
        console.error('Error loading existing location:', error);
      }
    }
  }, [location.state, mapReady]);

  const handleCreated = (e) => {
    const { layer, layerType } = e;
    
    // Convert circle to polygon (GeoJSON doesn't support circles)
    let actualLayer = layer;
    if (layerType === 'circle') {
      // Convert circle to a polygon with points approximating the circle
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      const points = 64; // Number of points to approximate circle
      const latlngs = [];
      
      for (let i = 0; i < points; i++) {
        const angle = (i * 360) / points;
        const lat = center.lat + (radius / 111320) * Math.cos((angle * Math.PI) / 180);
        const lng = center.lng + (radius / (111320 * Math.cos((center.lat * Math.PI) / 180))) * Math.sin((angle * Math.PI) / 180);
        latlngs.push([lat, lng]);
      }
      
      // Remove the circle and add a polygon
      actualLayer = L.polygon(latlngs, {
        color: '#ff7800',
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0.35
      });
      
      // Remove circle from map and add polygon
      if (featureGroupRef.current) {
        featureGroupRef.current.removeLayer(layer);
        featureGroupRef.current.addLayer(actualLayer);
      }
    }
    
    const geojson = actualLayer.toGeoJSON();
    const newLayer = { id: Date.now(), layer: actualLayer, geojson };
    setDrawnLayers(prev => [...prev, newLayer]);
    
    // Update selected geometry to include all layers
    updateSelectedGeometry([...drawnLayers, newLayer]);
  };

  // Update selected geometry based on all drawn layers
  const updateSelectedGeometry = (layers) => {
    if (layers.length === 0) {
      setSelectedGeometry(null);
      return;
    }
    
    if (layers.length === 1) {
      // Single feature
      setSelectedGeometry(layers[0].geojson.geometry);
    } else {
      // Multiple features - create GeometryCollection
      const geometries = layers.map(l => l.geojson.geometry);
      setSelectedGeometry({
        type: 'GeometryCollection',
        geometries: geometries
      });
    }
  };

  const handleEdited = (e) => {
    const layers = e.layers;
    const updatedLayers = [...drawnLayers];
    
    layers.eachLayer((layer) => {
      const geojson = layer.toGeoJSON();
      // Update the drawn layer
      const index = updatedLayers.findIndex(item => item.layer === layer);
      if (index !== -1) {
        updatedLayers[index] = { ...updatedLayers[index], geojson };
      }
    });
    
    setDrawnLayers(updatedLayers);
    updateSelectedGeometry(updatedLayers);
  };

  const handleDeleted = (e) => {
    const layers = e.layers;
    let updatedLayers = [...drawnLayers];
    
    layers.eachLayer((layer) => {
      updatedLayers = updatedLayers.filter(item => item.layer !== layer);
    });
    
    setDrawnLayers(updatedLayers);
    updateSelectedGeometry(updatedLayers);
  };

  const handleSaveLocation = () => {
    if (!selectedGeometry) {
      alert('Please draw a location on the map first.');
      return;
    }

    const geoJsonFeature = {
      type: "Feature",
      geometry: selectedGeometry,
      properties: {
        name: "Project Location",
        created: new Date().toISOString()
      }
    };

    // Pass the GeoJSON data back to the calling page
    if (location.state?.returnPath) {
      navigate(location.state.returnPath, {
        state: {
          locationData: geoJsonFeature,
          projectId: location.state?.projectId,
          returnPath: location.state.returnPath
        }
      });
    } else {
      navigate('/staff/consultation', {
        state: {
          locationData: geoJsonFeature
        }
      });
    }
  };

  const handleCancel = () => {
    const returnPath = location.state?.returnPath || '/staff/consultation';
    const projectId = location.state?.projectId;
    
    if (projectId) {
      // Return with projectId so the form can restore editing state
      navigate(returnPath, {
        state: { 
          cancelledFromPicker: true,
          projectId: projectId 
        }
      });
    } else {
      navigate(returnPath);
    }
  };

  const clearAll = () => {
    setDrawnLayers([]);
    setSelectedGeometry(null);
  };

  return (
    <div className="location-picker-container">
      <div className="location-picker-header">
        <div className="header-content">
          <h1>📍 Select Project Location</h1>
          <p>Draw points, lines, or areas on the map to define the project location</p>
        </div>
        
        <div className="location-picker-controls">
          <button 
            className="btn-secondary"
            onClick={clearAll}
            disabled={drawnLayers.length === 0}
          >
            🗑️ Clear All
          </button>
          <button 
            className="btn-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={handleSaveLocation}
            disabled={!selectedGeometry}
          >
            ✅ Save Location
          </button>
        </div>
      </div>

      <div className="location-picker-content">
        <div className="location-picker-instructions">
          <h3>🛠️ How to Use</h3>
          <div className="instructions-grid">
            <div className="instruction-item">
              <span className="icon">📍</span>
              <span>Use the <strong>marker</strong> tool for point locations</span>
            </div>
            <div className="instruction-item">
              <span className="icon">📏</span>
              <span>Use the <strong>line</strong> tool for routes or boundaries</span>
            </div>
            <div className="instruction-item">
              <span className="icon">🔷</span>
              <span>Use <strong>polygon/rectangle</strong> for areas or regions</span>
            </div>
            <div className="instruction-item">
              <span className="icon">⭕</span>
              <span>Use the <strong>circle</strong> tool for circular areas</span>
            </div>
            <div className="instruction-item">
              <span className="icon">✏️</span>
              <span>Click the <strong>edit</strong> tool to modify existing shapes</span>
            </div>
            <div className="instruction-item">
              <span className="icon">🗑️</span>
              <span>Use the <strong>delete</strong> tool to remove shapes</span>
            </div>
          </div>
        </div>

        <div className="map-container">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          whenReady={(map) => {
            console.log('Map is ready');
            setTimeout(() => {
              map.target.invalidateSize();
              setMapReady(true);
            }, 100);
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topright"
              onCreated={handleCreated}
              onEdited={handleEdited}
              onDeleted={handleDeleted}
              draw={{
                rectangle: {
                  shapeOptions: {
                    color: '#ff7800',
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0.35
                  }
                },
                polygon: {
                  allowIntersection: false,
                  shapeOptions: {
                    color: '#ff7800',
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0.35
                  }
                },
                circle: {
                  shapeOptions: {
                    color: '#ff7800',
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0.35
                  }
                },
                polyline: {
                  shapeOptions: {
                    color: '#ff7800',
                    weight: 3,
                    opacity: 0.8
                  }
                },
                marker: true,
                circlemarker: false
              }}
              edit={{
                featureGroup: featureGroupRef
              }}
            />
          </FeatureGroup>
        </MapContainer>
      </div>

      {selectedGeometry && (
        <div className="location-preview">
          <h3>📋 Location Data Preview</h3>
          <div className="geojson-preview">
            <pre>{JSON.stringify(selectedGeometry, null, 2)}</pre>
          </div>
          <p className="preview-note">
            This GeoJSON geometry will be saved with your project
          </p>
        </div>
      )}
      </div>
    </div>
  );
};

export default LocationPickerPage;